import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue } from "@utils/lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QueryRequestHelper } from "@_common/query-request-helper";
import moment from "moment";
import Loader from "@components/common/Loader";
import ManageAuditNotfound from "@components/Page-Not-Found/manage-audit-not-found";
import {
  getComplanceScoreCardInnerColumn,
  getLineItemWiseInnerColumn,
  getLocationInnerColumns,
  getRegionInnerColumns,
  getVendorInnerColumns,
  ScoreBar,
} from "./MISTaskColumns";
import { NavLink } from "react-router-dom";
import ScrollableReactTable from "@components/ScrollableDatatable";
import {
  downloadRetrificationReport,
  generateManageAuditVendorReport,
  getMISDetails,
  getMISLineItemWiseDetail,
} from "@services/audit.service";
import { getVendorCheckpointsAllDocuments } from "@services/checkpoint.service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { currentDate } from "@_common/date-helpers";
import { cleanPayload, PAGE_FILTERS } from "../Constant/Util";
import RightMenuHeaders from "@components/common/RightMenuHeaders";
import { handleDownloadOnClick } from "@_common/downloadfile";
import { useLocation } from "react-router-dom";

const MISTaskList: React.FunctionComponent = (props: any) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sites, setSites] = useState<any[]>([]);
  const [overviewData, setOverviewData] = useState<any | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const getParam = useCallback(
    (key: string) => searchParams.get(key) ?? "",
    [searchParams],
  );
  const pageNo = parseInt(getParam("page_no") || "1", 10);
  const limitSize = parseInt(getParam("limit") || "100", 10);
  const regionId = getParam("region_id");
  const groupId = getParam("group_id");
  const dateParam = getParam("date");
  const viewType = getParam("view_type");

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const getParam = (key: string): string =>
        searchParams.get(key)?.trim() ?? "";
      const rawDate = getParam("date");
      const selectedDate = rawDate ? moment(rawDate, "YYYY-MM-DD") : moment();
      const month = selectedDate.format("MM");
      const year = selectedDate.format("YYYY");
      const userSpec = getValue(localStorage, "user", "{}");
      const parsedUser =
        typeof userSpec === "string" ? JSON.parse(userSpec) : userSpec;
      const userId = getValue(parsedUser, "id", "");
      const typeMap: Record<string, string> = {
        LOCATION: "SITE_HISTORY",
        VENDOR: "YEAR_HISTORY",
        LINE_ITEM_WISE_REPORT: "LINE_ITEM_REPORT",
        COMPLIANCE_SCORECARD: "COMPLIANCE_SCORECARD_DETAILS",
      };
      const mappedType = typeMap[viewType] || viewType;
      const isLineItemReport = mappedType === "LINE_ITEM_REPORT";
      const payload = cleanPayload(
        isLineItemReport
          ? {
              checkpoint_id: getParam("checkpoint_id"),
              legislation_id: getParam("legislation_id"),
              month,
              year,
              type: mappedType,
              city_id: getParam("city_id") || undefined,
            }
          : {
              month,
              year,
              type: mappedType ? mappedType : "YEAR_HISTORY",
              user_id: userId,
              region_id: getParam("region_id") || undefined,
              group_id: getParam("group_id") || undefined,
              location_id: getParam("location_id") || undefined,
              vendor_id: getParam("vendor_id") || undefined,
              audit_type: getParam("audit_type") || undefined,
              page_no: pageNo,
              page_size: limitSize,
              is_redirect: true,
            },
      );
      const apiCall = isLineItemReport
        ? getMISLineItemWiseDetail
        : getMISDetails;
      const res = await apiCall(QueryRequestHelper(payload));
      const isSuccess = res?.statusCode === 200 || res?.statusCode === 201;
      if (!isSuccess) {
        toast.error(res?.message || "Failed to fetch data");
        return;
      }
      const responseData = res?.data ?? {};
      switch (viewType) {
        case "REGION":
        case "GROUP": {
          setSites(responseData.locations ?? []);
          setOverviewData(responseData.overview ?? null);
          break;
        }
        case "YEAR_HISTORY":
        case "VENDOR": {
          setSites(responseData.year_history ?? []);
          setOverviewData(null);
          break;
        }
        case "LINE_ITEM_WISE_REPORT": {
          const data = responseData?.checkpoints;
          const formatted: any[] = [];
          let snoCounter = 1;
          const labelMap: Record<string, string> = {
            NON_COMPLIED: "Non Compliant",
            NOT_APPLICABLE: "Not Applicable",
            NOT_SUBMITTED: "Not Submitted",
            COMPLIED: "Compliant",
            NC: "Non Compliant",
            NA: "Not Applicable",
            C: "Compliant",
          };
          data.forEach((checkpointObj: any) => {
            const statusKey = Object.keys(checkpointObj)[0];
            const items = checkpointObj[statusKey];
            if (Array.isArray(items) && items.length > 0) {
              formatted.push({
                _type: "status_header",
                label:
                  labelMap[statusKey] ||
                  statusKey
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
              });
              items.forEach((item) => {
                formatted.push({
                  ...item,
                  _type: "data_row",
                  _sno: snoCounter++,
                });
              });
            }
          });
          setSites(formatted ?? []);
          setOverviewData(null);
          break;
        }
        case "COMPLIANCE_SCORECARD": {
          const listData: any[] = [];
          (res.data as any[]).forEach((city: any) => {
            listData.push({ _type: "city_header", label: city.city_name });
            listData.push({ _type: "column_header" });
            (city.location || []).forEach((loc: any) => {
              listData.push({
                ...loc,
                _type: "data",
                city_name: city.city_name,
              });
            });
          });
          setSites(listData);
          setOverviewData({});
          break;
        }
        default: {
          setSites(responseData.vendors ?? []);
          setOverviewData(responseData.overview ?? null);
          break;
        }
      }
    } catch (error: any) {
      if (error?.name !== "AbortError" && error?.code !== "ERR_CANCELED") {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [limitSize, pageNo, viewType]);

  useEffect(() => {
    if (!props.permissionAPITriggered) return;
    fetchData();
    return () => abortRef.current?.abort();
  }, [props.permissionAPITriggered, regionId, groupId, dateParam, fetchData]);

  const handleDocs = async (vendor_id: string) => {
    const rawDate = getParam("date");
    const [year, month] = rawDate
      ? rawDate.split("-")
      : [moment().format("YYYY"), moment().format("MM")];
    const payload = {
      month: month,
      year: year,
      vendor_id: vendor_id,
      location_id: getParam("location_id"),
      report_type: "VENDOR_CHECKPOINT_ALL_DOCS",
      report_name: `Vendor_Checkpoint_DOCS_ZIP_${currentDate()}`,
    };
    const query = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      const resp = await getVendorCheckpointsAllDocuments(query);
      if (resp) {
        toast.success(getValue(resp, "message", "Zip generation started."));
        navigate(`/self-compliance-logs/VENDOR_CHECKPOINT_ALL_DOCS`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExcel = async (vendor_id: string) => {
    const rawDate = getParam("date");
    const [year, month] = rawDate
      ? rawDate.split("-")
      : [moment().format("YYYY"), moment().format("MM")];
    const payload: any = {
      report_type: "VENDOR_AUDIT_SCORE",
      report_name: `vendor_audit_score_${currentDate()}`,
      month: parseInt(month),
      year: year,
      region_id: parseInt(getParam("region_id")),
      group_id: getParam("group_id"),
      location_id: getParam("location_id"),
      vendor_id: vendor_id,
      audit_type: getParam("audit_type"),
    };
    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === undefined ||
        payload[key] === "" ||
        payload[key] === null
      ) {
        delete payload[key];
      }
    });
    try {
      setIsLoading(true);
      const resp = await generateManageAuditVendorReport(payload);
      if (resp) {
        toast.success(getValue(resp, "message", "Report generation started."));
        navigate(`/self-compliance-logs/VENDOR_AUDIT_SCORE`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSiteClick = useCallback(
    (rowData: any) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("view_type", "LOCATION");
      nextParams.set("location_id", rowData.location_id);
      if (rowData.region_id) {
        nextParams.set("region_id", rowData.region_id);
      }
      if (rowData.group_id) {
        nextParams.set("group_id", rowData.group_id);
      }
      navigate(`${window.location.pathname}?${nextParams.toString()}`, {
        state: {
          previousPath: `${window.location.pathname}?${searchParams.toString()}`,
        },
      });
    },
    [searchParams, navigate],
  );

  const columns = useMemo(() => {
    if (viewType === "REGION" || viewType === "GROUP") {
      return getRegionInnerColumns(handleSiteClick);
    } else if (viewType === "VENDOR" || viewType === "YEAR_HISTORY") {
      const baseDate = getParam("date")
        ? new Date(getParam("date"))
        : new Date();
      const year = baseDate.getFullYear();
      return getVendorInnerColumns(year);
    } else if (viewType === "LINE_ITEM_WISE_REPORT") {
      return getLineItemWiseInnerColumn;
    } else if (viewType === "COMPLIANCE_SCORECARD") {
      return getComplanceScoreCardInnerColumn;
    }
    return getLocationInnerColumns(handleExcel, handleDocs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getParam, handleExcel, handleDocs, handleSiteClick]);

  const handleDownloadInnerReport = useCallback(async () => {
    const rawDate = getParam("date");
    const selectedDate = rawDate ? moment(rawDate, "YYYY-MM-DD") : moment();
    const month = selectedDate.format("MM");
    const year = selectedDate.format("YYYY");
    const userSpec = getValue(localStorage, "user", "{}");
    const parsedUser =
      typeof userSpec === "string" ? JSON.parse(userSpec) : userSpec;
    const userId = getValue(parsedUser, "id", "");
    const reportConfigMap: Record<
      string,
      {
        report_type: string;
        report_name: string;
        logPath: string;
      }
    > = {
      VENDOR: {
        report_type: "MIS_SITE_YEAR_HISTORY_REPORT",
        report_name: `MIS_SITE_YEAR_HISTORY_REPORT_${currentDate()}`,
        logPath: "MIS_SITE_YEAR_HISTORY_REPORT",
      },
      YEAR_HISTORY: {
        report_type: "MIS_SITE_YEAR_HISTORY_REPORT",
        report_name: `MIS_SITE_YEAR_HISTORY_REPORT_${currentDate()}`,
        logPath: "MIS_SITE_YEAR_HISTORY_REPORT",
      },
      REGION: {
        report_type: "MIS_REGION_DETAIL_REPORT",
        report_name: `MIS_REGION_DETAIL_REPORT_${currentDate()}`,
        logPath: "MIS_REGION_DETAIL_REPORT",
      },
      GROUP: {
        report_type: "MIS_ACCOUNT_DETAIL_REPORT",
        report_name: `MIS_ACCOUNT_DETAIL_REPORT_${currentDate()}`,
        logPath: "MIS_ACCOUNT_DETAIL_REPORT",
      },
    };
    const currentReportConfig =
      reportConfigMap[viewType] || reportConfigMap.VENDOR;
    const payload = {
      month,
      year,
      type: viewType === "VENDOR" ? "YEAR_HISTORY" : viewType,
      user_id: userId,
      region_id: getParam("region_id") || undefined,
      group_id: getParam("group_id") || undefined,
      location_id: getParam("location_id") || undefined,
      city_id:getParam("city_id") || undefined,
      vendor_id: getParam("vendor_id") || undefined,
      report_type: currentReportConfig.report_type,
      report_name: currentReportConfig.report_name,
      is_redirect: true,
    };
    setIsLoading(true);
    try {
      const resp = await downloadRetrificationReport(cleanPayload(payload));
      if (resp) {
        toast.success(
          getValue(resp, "message", "Export triggered successfully"),
        );
        const downloadUrl = getValue(resp, "data.downloadUrl", "");
        if (downloadUrl) {
          handleDownloadOnClick(downloadUrl, "Audit_Report");
        }
        navigate(`/self-compliance-logs/${currentReportConfig.logPath}`);
      }
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setIsLoading(false);
    }
  }, [viewType, getParam, navigate]);

  return (
    <DashboardLayout
      permissions={getValue(props, "tabPermissionList", [])}
      subTabPermissionList={getValue(props, "subTabPermissionList", [])}
    >
      <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <div className="header-parent-icon-holder">
            <NavLink
              to={
                location.state?.previousPath ||
                `/audit/mis-reports?${(() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete("region_id");
                  p.delete("group_id");
                  p.delete("location_id");
                  p.delete("vendor_id");
                  return p.toString();
                })()}`
              }
            >
              <img
                src="/images/back-link.svg"
                className="img-fluid ml-5"
                height={24}
                width={24}
                alt=""
              />
            </NavLink>
          </div>
          <div className="title-wrapper me-2">
            <h6 className="dashbaord-wrapper__header-parent-title">
              MIS Report
            </h6>
            <h4 className="dashbaord-wrapper__header-title">
              {" "}
              {PAGE_FILTERS.find((f) => f.value === getParam("view_type"))
                ?.label || "Site Audit Report"}{" "}
              Detail
            </h4>
          </div>
        </div>

        <div className="d-flex align-items-center">
          <RightMenuHeaders
            monthYear={true}
            isYear={true}
            value={dateParam ? new Date(dateParam) : new Date()}
            onChange={(newDate: Date) => {
              const formatted = moment(newDate).format("YYYY-MM-DD");
              const next = new URLSearchParams(searchParams);
              next.set("date", formatted);
              navigate(`${window.location.pathname}?${next.toString()}`, {
                replace: true,
              });
            }}
            lineSeperator
            downloadLoading={isLoading}
            downloadSelfComplianceReport={[
              "GROUP",
              "REGION",
              "VENDOR",
              "YEAR_HISTORY",
            ].includes(getParam("view_type") as string)}
            handleDownloadSelfComplianceReport={handleDownloadInnerReport}
            viewDownloadedSelfComplianceReports={[
              "GROUP",
              "REGION",
              "VENDOR",
              "YEAR_HISTORY",
            ].includes(getParam("view_type") as string)}
            handleNavigateLogs={() => {
              const vt = getParam("view_type");
              const reportTypeMap: Record<string, string> = {
                LOCATION: "LOCATION_MIS_AUDIT_SCORE_REPORT",
                REGION: "REGION_MIS_AUDIT_SCORE_REPORT",
                GROUP: "GROUP_MIS_AUDIT_SCORE_REPORT",
                VENDOR: "VENDOR_MIS_AUDIT_SCORE_REPORT",
                YEAR_HISTORY: "MIS_SITE_YEAR_HISTORY_REPORT",
              };
              navigate(`/self-compliance-logs/${reportTypeMap[vt]}`);
            }}
            isApplyButtonRequied={false}
          />
        </div>
      </div>

      <div className="">
        {isLoading || !props.permissionAPITriggered ? (
          <Loader />
        ) : sites.length === 0 ? (
          <ManageAuditNotfound />
        ) : (
          <div className="card mb-4 shadow-sm border">
            {["REGION", "GROUP"].includes(getParam("view_type")) ? (
              <div
                className="p-3 text-dark d-flex flex-wrap align-items-center"
                style={{ backgroundColor: "#18cae6", fontSize: "14px" }}
              >
                <div className="me-3">
                  <strong>Detail MIS</strong>{" "}
                  {overviewData.region_name || overviewData.group_name || ""}
                </div>
                <div className="me-3">
                  <strong>Audit Month</strong>{" "}
                  {getParam("date")
                    ? moment(getParam("date")).format("MMMM-YYYY")
                    : moment().format("MMMM-YYYY")}
                </div>
                <div className="me-3">
                  <strong>Total Sites</strong> {overviewData.location || 0}
                </div>
                <div>
                  <strong>Total Vendor Audits</strong>{" "}
                  {overviewData.vendor_count || 0}
                </div>
              </div>
            ) : getParam("view_type") === "VENDOR" ||
              getParam("view_type") === "YEAR_HISTORY" ? (
              <div
                className="p-3 text-dark d-flex flex-wrap align-items-center"
                style={{ backgroundColor: "#18cae6", fontSize: "14px" }}
              >
                <div className="me-3">
                  <strong>Vendor Year History</strong>{" "}
                  {overviewData?.vendor_name || ""}
                </div>
                <div className="me-3">
                  <strong>Year</strong>{" "}
                  {getParam("date")
                    ? getParam("date").split("-")[0]
                    : moment().format("YYYY")}
                </div>
                {overviewData?.location_name && (
                  <div className="me-3">
                    <strong>Site</strong> {overviewData?.location_name || ""}
                  </div>
                )}
              </div>
            ) : getParam("view_type") === "LINE_ITEM_WISE_REPORT" ? (
              <div
                className="p-3 text-white"
                style={{
                  backgroundColor: "#18cae6",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Item Wise NC Detail
              </div>
            ) : getParam("view_type") === "COMPLIANCE_SCORECARD" ? (
              <div
                className="p-3 text-dark d-flex flex-wrap align-items-center"
                style={{ backgroundColor: "#18cae6", fontSize: "14px" }}
              >
                <div className="me-3">
                  <strong>Compliance Scorecard Detail</strong>
                </div>
                <div className="me-3">
                  <strong>Audit Month</strong>{" "}
                  {getParam("date")
                    ? moment(getParam("date")).format("MMMM-YYYY")
                    : moment().format("MMMM-YYYY")}
                </div>
                <div>
                  <strong>Total Sites</strong> {sites.length}
                </div>
              </div>
            ) : (
              <>
                <div className="card-body border-bottom pb-3  bg-light">
                  <h5 className="fw-bold mb-2">
                    {overviewData?.location_name} | {overviewData?.city_name} |
                    Audit Month: {overviewData?.month}
                  </h5>

                  {overviewData?.address1 && (
                    <p className="mb-1 small">
                      <strong>Address:</strong> {overviewData.address1}
                      {""}
                      {overviewData?.address2}
                    </p>
                  )}

                  <p className="fw-bold mb-1">
                    Total Manpower : {overviewData?.headcount}
                  </p>

                  {overviewData?.month && overviewData?.year && (
                    <p className="text-muted small mb-0">
                      Audit conducted on{" "}
                      {moment(overviewData.month, "MM").format("MMMM")}{" "}
                      {overviewData.year}
                    </p>
                  )}
                </div>

                <div className="card-body bg-light border-bottom py-3">
                  <ScoreBar
                    label="Full Score"
                    score={overviewData?.overall_full_score ?? 0}
                  />
                  <ScoreBar
                    label="Critical Score"
                    score={overviewData?.overall_critical_score ?? 0}
                  />
                </div>
              </>
            )}

            <div className="card-body p-0">
              <ScrollableReactTable
                columns={columns}
                data={sites}
                getTableState={() => {}}
                rowClick={() => {}}
                checkboxlogo
                check={false}
                selectedIDs={[]}
                getTrProps={() => ({})}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MISTaskList;
